import { apiFetch } from "./client";
import type {
  AccountContactsResponse,
  ContactMethodsResponse,
  ContactSearchResponse,
  ContactCreatePayload,
  ContactCreateResult,
  SetPrimaryContactResponse,
} from "./types";

export function fetchAccountContacts(
  accountId: string,
  limit?: number,
  signal?: AbortSignal,
): Promise<AccountContactsResponse> {
  return apiFetch<AccountContactsResponse>(
    `/Custom/frontend/account/${encodeURIComponent(accountId)}/contacts`,
    { query: { limit }, signal },
  );
}

export function fetchContactMethods(
  contactId: string,
  accountContext: string | undefined,
  signal?: AbortSignal,
): Promise<ContactMethodsResponse> {
  return apiFetch<ContactMethodsResponse>(
    `/Custom/frontend/contact/${encodeURIComponent(contactId)}/methods`,
    { query: { accountContext }, signal },
  );
}

export function searchContacts(
  q: string,
  contextType: "account" | "holding" | "outlet" | undefined,
  contextId: string | undefined,
  signal?: AbortSignal,
): Promise<ContactSearchResponse> {
  return apiFetch<ContactSearchResponse>("/Custom/contacts/search", {
    query: { q, contextType, contextId },
    signal,
  });
}

/**
 * Atomic create. Failure preserves the user's input — caller keeps the
 * drawer open and surfaces field/step errors from the response.
 */
export function createContact(
  body: ContactCreatePayload,
): Promise<ContactCreateResult> {
  return apiFetch<ContactCreateResult>("/Custom/frontend/contact", {
    method: "POST",
    body,
  });
}

export function setPrimaryContact(
  accountId: string,
  contactId: string,
): Promise<SetPrimaryContactResponse> {
  return apiFetch<SetPrimaryContactResponse>(
    `/Custom/frontend/account/${encodeURIComponent(accountId)}/contacts/${encodeURIComponent(contactId)}/set-primary`,
    { method: "POST", body: {} },
  );
}

// ── Phase 3.X-1B sub_1 — Contact LIST (GET /Custom/frontend/contacts) ─────────
//
// Backend owns ACL/scope, cursor pagination, facets, search, ordering (TZ §8).
// §E2: restricted methods/contacts never reach the client — hasPhone/hasEmail
// are visible-only indicators; no values, no hidden counts.

export type ContactScope = "my" | "team" | "all";

export interface ContactListRow {
  id: string;
  /** Header name — first/last (TZ §8 contactDisplayName). */
  contactDisplayName: string;
  position: string;
  roleCode: string;
  roleLabel: string;
  /** Context chip — A-block client (holding) name; null when none. */
  clientId: string | null;
  clientDisplayName: string | null;
  manager: string;
  dataQualityStatus: string;
  needsReview: boolean;
  isActive: boolean;
  /** cCPrimaryContact — drives the «Основний» badge (custom view). */
  isPrimary: boolean;
  hasPhone: boolean;
  hasEmail: boolean;
  hasOpenTasks: boolean;
  hasDuplicates: boolean;
  /** Count of duplicate SUGGESTIONS (candidates, not merged) — GitMerge badge. */
  duplicateCount: number;
}

export interface ContactListFacets {
  needsReview: number;
  duplicates: number;
  hasOpenTasks: number;
  hasMethods: number;
}

export interface ContactListResponse {
  items: ContactListRow[];
  nextCursor: string | null;
  totalVisibleCount: number;
  allowedScopes: ContactScope[];
  defaultScope: ContactScope;
  appliedScope: ContactScope;
  managers: Array<{ id: string; name: string }>;
  facets: ContactListFacets;
}

export interface ContactListQuery {
  cursor?: string;
  limit?: number;
  q?: string;
  scope?: ContactScope | "";
  manager?: string;
  hasMethods?: boolean;
  hasOpenTasks?: boolean;
  needsReview?: boolean;
  duplicateStatus?: boolean;
}

export function fetchContactList(
  q: ContactListQuery = {},
  signal?: AbortSignal,
): Promise<ContactListResponse> {
  return apiFetch<ContactListResponse>("/Custom/frontend/contacts", {
    query: {
      cursor: q.cursor || undefined,
      limit: q.limit,
      q: q.q || undefined,
      scope: q.scope || undefined,
      manager: q.manager || undefined,
      hasMethods: q.hasMethods ? "1" : undefined,
      hasOpenTasks: q.hasOpenTasks ? "1" : undefined,
      needsReview: q.needsReview ? "1" : undefined,
      duplicateStatus: q.duplicateStatus ? "1" : undefined,
    },
    signal,
  });
}

// ── Phase 3.X-1B sub_2 — Contact DETAIL (envelope + lazy tabs, A2 pattern) ─────

export interface ContactMethodItem {
  id: string;
  type: string;
  value: string;
  label: string;
  isPrimary: boolean;
  isPreferred: boolean;
  channel: "phone" | "email" | "messenger" | "other";
  verifiedAt: string | null;
  source: string;
}

export interface ContactDetailEnvelope {
  restricted: boolean;
  identity: {
    id: string;
    contactDisplayName: string;
    firstName: string;
    lastName: string;
    position: string;
    roleCode: string;
    roleLabel: string;
    /** cCPrimaryContact — «Основний» badge. */
    isPrimary: boolean;
    /** cCRole === 'decision-maker' — «Особа, що приймає рішення» badge. */
    isDecisionMaker: boolean;
    /** Contact.description — «Нотатки (CRM)» in the Overview tab. */
    notes: string;
    dataQualityStatus: string;
    needsReview: boolean;
    isActive: boolean;
    marketingConsent: string;
    doNotCall: boolean;
    doNotEmail: boolean;
    doNotMessage: boolean;
  };
  context: {
    clientId: string | null;
    clientDisplayName: string | null;
    /** Representative account name — for the holding→account «Прив'язки» line. */
    accountDisplayName: string | null;
  };
  methodsSummary: { primaryVisible: ContactMethodItem[]; totalVisibleCount: number };
  mergeStatus: {
    isCanonical: boolean;
    canonicalOfCount: number;
    isMergedInto: { id: string; name: string } | null;
    duplicateVisibleCount: number;
  };
  signedContracts: { count: number };
  allowedActions: {
    canEdit: boolean;
    canAddMethod: boolean;
    canViewMethods: boolean;
    canViewAccounts: boolean;
    canViewActivities: boolean;
    canViewContracts: boolean;
    canMergeContact: boolean;
  };
  tabAvailability: {
    identity: boolean;
    methods: boolean;
    accounts: boolean;
    activities: boolean;
    contracts: boolean;
    mergeStatus: boolean;
  };
  meta: { updatedAt: string; partial: boolean; warnings: string[] };
}

export type ContactTabKey = "methods" | "accounts" | "activities" | "contracts" | "merge-status";

export interface ContactTabResponse<T = Record<string, unknown>> {
  items: T[];
  restricted?: boolean;
  nextCursor?: string | null;
  totalVisibleCount?: number;
  // merge-status extras
  isCanonical?: boolean;
  canonicalOfCount?: number;
  isMergedInto?: { id: string; name: string } | null;
  canMergeContact?: boolean;
}

export interface ContactAccountItem {
  id: string;
  targetType: "account" | "holding" | "outlet";
  targetName: string;
  clientId: string | null;
  role: string;
  isPrimary: boolean;
}

export interface ContactActivityItem {
  id: string;
  type: string;
  typeLabel: string;
  occurredAt: string | null;
  summary: string;
  outcome: string;
  accountName: string;
}

export interface ContactSignedContractItem {
  id: string;
  contractNumber: string;
  accountName: string;
  currentState: string;
  stateLabel: string;
  effectiveDate: string | null;
  expirationDate: string | null;
  managed: string;
}

export interface ContactMergeCandidate {
  id: string;
  otherContactId: string;
  otherContactName: string;
  status: string;
  confidence: string;
  score: number | null;
}

export function fetchContactDetail(
  contactId: string,
  signal?: AbortSignal,
): Promise<ContactDetailEnvelope> {
  return apiFetch<ContactDetailEnvelope>(
    `/Custom/frontend/contacts/${encodeURIComponent(contactId)}`,
    { signal },
  );
}

export function fetchContactTab<T = Record<string, unknown>>(
  contactId: string,
  tab: ContactTabKey,
  cursor?: string,
  signal?: AbortSignal,
): Promise<ContactTabResponse<T>> {
  return apiFetch<ContactTabResponse<T>>(
    `/Custom/frontend/contacts/${encodeURIComponent(contactId)}/${tab}`,
    { query: { cursor: cursor || undefined }, signal },
  );
}
